import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, CalendarRange, List, ChevronLeft, ChevronRight, Copy, ArrowRight, CalendarDays, Calendar, CalendarPlus, Repeat, AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import TrainingTextImportDialog from "@/components/TrainingTextImportDialog";
import BulkProgramDialog from "@/components/BulkProgramDialog";
import DuplicateTrainingDialog from "@/components/DuplicateTrainingDialog";
import DuplicateWeekDialog from "@/components/DuplicateWeekDialog";
import CopyDayDialog from "@/components/CopyDayDialog";
import RecurrenceDialog from "@/components/RecurrenceDialog";
import ICalShareDialog from "@/components/ICalShareDialog";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

// Parsa una data: le stringhe "YYYY-MM-DD" (date-only) SEMPRE come data LOCALE,
// mai come timestamp UTC → 2026-09-04 resta 4 settembre in ogni timezone. Nessun +1/-1.
function toLocalDate(v) {
  if (v instanceof Date) return new Date(v.getTime()); // clone: mai mutare l'input
  if (typeof v === "string") {
    const p = v.slice(0, 10).split("-");
    if (p.length === 3 && p[0] && p[1] && p[2]) return new Date(+p[0], +p[1] - 1, +p[2]);
  }
  return new Date(v);
}
function mondayOf(date) {
  const d = toLocalDate(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0, 0, 0, 0);
  return d;
}
function firstOfMonth(date) { const d = toLocalDate(date); d.setDate(1); d.setHours(0,0,0,0); return d; }
function addDays(date, n) { const d = toLocalDate(date); d.setDate(d.getDate() + n); return d; }
function addMonths(date, n) { const d = toLocalDate(date); d.setMonth(d.getMonth() + n); return d; }
function iso(date) { const d = toLocalDate(date); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const dd = String(d.getDate()).padStart(2, "0"); return `${y}-${m}-${dd}`; }
const DUR_PRESETS = [45, 60, 75, 90, 105, 120];
const emptyForm = () => ({ title: "", date: iso(new Date()), duration: 90, intensity: "media", objective: "", notes: "", group_ids: [] });
function monthLabel(d) { return d.toLocaleDateString("it-IT", { month: "long", year: "numeric" }); }

const INTENSITY_STYLES = {
  bassa: "border-l-4 border-l-emerald-500",
  media: "border-l-4 border-l-amber-500",
  alta: "border-l-4 border-l-red-500",
};
const INTENSITY_DOT = { bassa: "bg-emerald-500", media: "bg-amber-500", alta: "bg-red-500" };

export default function Training() {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [groups, setGroups] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("csp_training_view") || "week");
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [monthStart, setMonthStart] = useState(() => firstOfMonth(new Date()));
  const [filterGroup, setFilterGroup] = useState(() => localStorage.getItem("csp_training_filter_group") || "");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    const [t, g] = await Promise.all([api.get("/trainings"), api.get("/groups")]);
    setTrainings(t.data);
    setGroups(g.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem("csp_training_view", view); }, [view]);
  useEffect(() => { localStorage.setItem("csp_training_filter_group", filterGroup || ""); }, [filterGroup]);

  const filtered = useMemo(() => {
    if (!filterGroup) return trainings;
    return trainings.filter((t) => (t.group_ids || []).includes(filterGroup));
  }, [trainings, filterGroup]);

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Titolo obbligatorio"); return; }
    const dur = parseInt(form.duration || 0);
    if (Number.isNaN(dur) || dur < 15 || dur > 240) { toast.error("Durata non valida (15–240 min)"); return; }
    try {
      const payload = { ...form, duration: dur };
      if (editingId) {
        await api.put(`/trainings/${editingId}`, { ...payload, id: editingId });
        toast.success("Allenamento aggiornato");
      } else {
        await api.post("/trainings", payload);
        toast.success("Allenamento creato");
      }
      setOpen(false); setEditingId(null); setForm(emptyForm()); load();
    } catch { toast.error("Errore"); }
  };

  const openNew = () => { setForm(emptyForm()); setEditingId(null); setOpen(true); };
  const openEdit = (t) => {
    setForm({
      title: t.title || "", date: (t.date || "").slice(0, 10), duration: t.duration || 90,
      intensity: t.intensity || "media", objective: t.objective || "", notes: t.notes || "",
      group_ids: t.group_ids || [], athlete_ids: t.athlete_ids || [], blocks: t.blocks || [],
      coach_id: t.coach_id || null,
    });
    setEditingId(t.id); setOpen(true);
  };
  const askDelete = async (t) => {
    try {
      const { data } = await api.get(`/trainings/${t.id}/dependencies`);
      if (!data.deletable) { toast.error(`Impossibile eliminare: ${data.total} ${data.total === 1 ? "registro presenze collegato" : "registri presenze collegati"}.`); return; }
      setDeleteTarget(t);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/trainings/${deleteTarget.id}/safe`);
      toast.success("Allenamento eliminato");
      setDeleteTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };

  const duplicateNextWeek = async (t) => {
    try {
      await api.post(`/trainings/${t.id}/duplicate-next-week`);
      toast.success(`Copiato in ${iso(addDays(t.date, 7))}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const moveTraining = async (id, newDate) => {
    const t = trainings.find((x) => x.id === id);
    if (!t || t.date === newDate) return;
    try {
      await api.put(`/trainings/${id}`, { ...t, date: newDate });
      toast.success(`Spostato a ${newDate}`);
      load();
    } catch { toast.error("Errore spostamento"); }
  };

  // Week view data
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const byDay = weekDays.map((d) => {
    const key = iso(d);
    return { date: key, dayLabel: DAYS[d.getDay() === 0 ? 6 : d.getDay() - 1], list: filtered.filter((t) => (t.date || "").slice(0, 10) === key).sort((a,b) => (a.title||"").localeCompare(b.title||"")) };
  });

  // Month view data
  const monthCells = useMemo(() => {
    const first = firstOfMonth(monthStart);
    const gridStart = mondayOf(first);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = addDays(gridStart, i);
      const key = iso(d);
      cells.push({
        date: key,
        day: d.getDate(),
        inMonth: d.getMonth() === first.getMonth(),
        isToday: iso(d) === iso(new Date()),
        list: filtered.filter((t) => (t.date || "").slice(0, 10) === key),
      });
    }
    return cells;
  }, [monthStart, filtered]);

  const listSorted = [...filtered].sort((a,b) => (b.date||"").localeCompare(a.date||""));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Allenamento</h1>
          <p className="text-zinc-500 mt-1">Sessioni pianificate e storico.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-white border border-zinc-200 rounded-md p-1">
            <button data-testid="view-week" onClick={() => setView("week")} className={`px-3 py-2 text-sm rounded flex items-center gap-1 ${view === "week" ? "bg-black text-white" : "text-zinc-600"}`}><CalendarRange className="w-4 h-4" />Settimana</button>
            <button data-testid="view-month" onClick={() => setView("month")} className={`px-3 py-2 text-sm rounded flex items-center gap-1 ${view === "month" ? "bg-black text-white" : "text-zinc-600"}`}><CalendarDays className="w-4 h-4" />Mese</button>
            <button data-testid="view-list" onClick={() => setView("list")} className={`px-3 py-2 text-sm rounded flex items-center gap-1 ${view === "list" ? "bg-black text-white" : "text-zinc-600"}`}><List className="w-4 h-4" />Lista</button>
          </div>
          <Select value={filterGroup || "all"} onValueChange={(v) => setFilterGroup(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-group" className="h-12 w-[180px]"><SelectValue placeholder="Tutti i gruppi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i gruppi</SelectItem>
              {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <ICalShareDialog trigger={<Button data-testid="ical-btn" variant="outline" className="h-12"><Calendar className="w-4 h-4 mr-2" />iCal</Button>} />
          <DuplicateWeekDialog onDone={load} groups={groups} />
          <TrainingTextImportDialog onDone={load} groups={groups} />
          <BulkProgramDialog onDone={load} groups={groups} />
          <Button data-testid="new-training-btn" onClick={openNew} className="h-12 bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-2" />Nuovo</Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditingId(null); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>{editingId ? "Modifica allenamento" : "Nuovo allenamento"}</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Titolo</Label><Input data-testid="training-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} /></div>
                <div><Label>Data</Label><Input type="date" data-testid="training-date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></div>
                <div>
                  <Label>Durata</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="training-duration-picker">
                    {DUR_PRESETS.map((m) => (
                      <button key={m} type="button" data-testid={`dur-${m}`} onClick={() => setForm({...form, duration: m})}
                        className={`px-2.5 py-1.5 text-xs rounded border transition-colors ${form.duration === m ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}>{m}′</button>
                    ))}
                    <button type="button" data-testid="dur-custom" onClick={() => setForm({...form, duration: DUR_PRESETS.includes(form.duration) ? 100 : form.duration})}
                      className={`px-2.5 py-1.5 text-xs rounded border transition-colors ${!DUR_PRESETS.includes(form.duration) ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}>Personalizzata</button>
                  </div>
                  {!DUR_PRESETS.includes(form.duration) && (
                    <Input type="number" min={15} max={240} data-testid="dur-custom-input" className="mt-2 w-32"
                      value={form.duration} onChange={(e) => setForm({...form, duration: parseInt(e.target.value || 0)})} />
                  )}
                  <div className="text-[11px] text-zinc-400 mt-1">Range 15–240 min.</div>
                </div>
                <div>
                  <Label>Intensità</Label>
                  <Select value={form.intensity} onValueChange={(v) => setForm({...form, intensity: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bassa">Bassa</SelectItem>
                      <SelectItem value="media">Media</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Obiettivo</Label><Input data-testid="training-objective" value={form.objective} onChange={(e) => setForm({...form, objective: e.target.value})} /></div>
                <div><Label>Note</Label><Input data-testid="training-notes" value={form.notes || ""} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
                <div>
                  <Label>Gruppi</Label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="training-groups">
                    {groups.map((g) => {
                      const on = (form.group_ids || []).includes(g.id);
                      return (
                        <button key={g.id} type="button" data-testid={`tgroup-${g.id}`}
                          onClick={() => setForm({...form, group_ids: on ? form.group_ids.filter((x) => x !== g.id) : [...(form.group_ids || []), g.id]})}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}>{g.name}</button>
                      );
                    })}
                    {groups.length === 0 && <span className="text-xs text-zinc-400">Nessun gruppo</span>}
                  </div>
                </div>
              </div>
              <DialogFooter><Button data-testid="training-save" onClick={save} className="bg-black hover:bg-zinc-800">{editingId ? "Aggiorna" : "Salva"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {view === "week" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-white border border-zinc-200 rounded-md px-4 py-2">
            <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <div className="font-heading font-semibold text-sm">
              {iso(weekStart)} — {iso(addDays(weekStart, 6))}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="week-today">Oggi</Button>
              <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="week-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {byDay.map((d) => (
              <div
                key={d.date}
                data-testid={`day-${d.date}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragId) { moveTraining(dragId, d.date); setDragId(null); } }}
                className="bg-white border border-zinc-200 rounded-md min-h-[180px] p-2 flex flex-col gap-2"
              >
                <div className="flex items-center justify-between px-1">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{d.dayLabel}</div>
                    <div className="text-sm font-mono">{d.date.slice(8, 10)}/{d.date.slice(5, 7)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-mono text-zinc-400">{d.list.length}</span>
                    {d.list.length > 0 && (
                      <CopyDayDialog
                        fromDate={d.date}
                        groupId={filterGroup}
                        groups={groups}
                        onDone={load}
                        trigger={<button data-testid={`copyday-btn-${d.date}`} title="Copia giorno" className="text-zinc-400 hover:text-black p-0.5"><CalendarPlus className="w-3 h-3" /></button>}
                      />
                    )}
                  </div>
                </div>
                <div className="flex-1 space-y-1.5">
                  {d.list.map((t) => (
                    <div
                      key={t.id}
                      draggable
                      onDragStart={() => setDragId(t.id)}
                      onDragEnd={() => setDragId(null)}
                      onClick={() => navigate(`/allenamento/${t.id}`)}
                      data-testid={`week-training-${t.id}`}
                      className={`bg-zinc-50 rounded-md p-2 text-xs cursor-pointer hover:bg-zinc-100 ${INTENSITY_STYLES[t.intensity] || "border-l-4 border-l-zinc-300"}`}
                    >
                      <div className="font-medium block truncate">{t.title}</div>
                      <div className="text-zinc-500 font-mono text-[10px]">{t.duration}′ · {t.intensity}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <button data-testid={`dup-next-${t.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); duplicateNextWeek(t); }} className="text-[10px] text-[#0052FF] hover:underline flex items-center gap-1"><ArrowRight className="w-2.5 h-2.5" />+7g</button>
                        <DuplicateTrainingDialog training={t} onDone={load} trigger={
                          <button data-testid={`dup-btn-${t.id}`} className="text-[10px] text-zinc-600 hover:underline flex items-center gap-1"><Copy className="w-2.5 h-2.5" />Duplica</button>
                        } />
                        <RecurrenceDialog training={t} onDone={load} trigger={
                          <button data-testid={`rec-btn-${t.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} className="text-[10px] text-zinc-600 hover:underline flex items-center gap-1"><Repeat className="w-2.5 h-2.5" />Ripeti</button>
                        } />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "month" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-white border border-zinc-200 rounded-md px-4 py-2">
            <Button variant="outline" size="sm" onClick={() => setMonthStart(addMonths(monthStart, -1))} data-testid="month-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <div className="font-heading font-semibold text-sm capitalize">{monthLabel(monthStart)}</div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setMonthStart(firstOfMonth(new Date()))} data-testid="month-today">Oggi</Button>
              <Button variant="outline" size="sm" onClick={() => setMonthStart(addMonths(monthStart, 1))} data-testid="month-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-px bg-zinc-200 border border-zinc-200 rounded-md overflow-hidden">
            {DAYS.map((d) => (
              <div key={d} className="bg-white px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-500 font-mono text-center">{d}</div>
            ))}
            {monthCells.map((c) => (
              <div
                key={c.date}
                data-testid={`month-cell-${c.date}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragId) { moveTraining(dragId, c.date); setDragId(null); } }}
                className={`bg-white min-h-[110px] p-1.5 flex flex-col gap-1 ${c.inMonth ? "" : "opacity-40"} ${c.isToday ? "ring-2 ring-inset ring-black" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-mono ${c.isToday ? "font-bold" : "text-zinc-500"}`}>{c.day}</span>
                  {c.list.length > 0 && (
                    <CopyDayDialog fromDate={c.date} groupId={filterGroup} groups={groups} onDone={load}
                      trigger={<button data-testid={`month-copyday-${c.date}`} title="Copia giorno" className="text-zinc-300 hover:text-black p-0.5"><CalendarPlus className="w-3 h-3" /></button>}
                    />
                  )}
                </div>
                <div className="flex-1 space-y-1 overflow-hidden">
                  {c.list.slice(0, 3).map((t) => (
                    <div
                      key={t.id}
                      draggable
                      onDragStart={() => setDragId(t.id)}
                      onDragEnd={() => setDragId(null)}
                      onClick={() => navigate(`/allenamento/${t.id}`)}
                      data-testid={`month-training-${t.id}`}
                      className={`block bg-zinc-50 hover:bg-zinc-100 rounded p-1 text-[10px] truncate cursor-pointer ${INTENSITY_STYLES[t.intensity] || "border-l-4 border-l-zinc-300"}`}
                    >
                      <div className="flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${INTENSITY_DOT[t.intensity] || "bg-zinc-400"}`} />
                        <span className="font-medium truncate">{t.title}</span>
                      </div>
                    </div>
                  ))}
                  {c.list.length > 3 && <div className="text-[10px] text-zinc-400 font-mono">+{c.list.length - 3}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "list" && (
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
          <table className="w-full">
            <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <tr><th className="text-left px-5 py-3">Data</th><th className="text-left px-5 py-3">Titolo</th><th className="text-left px-5 py-3">Durata</th><th className="text-left px-5 py-3">Intensità</th><th className="text-right px-5 py-3 w-64"></th></tr>
            </thead>
            <tbody>
              {listSorted.map((t) => (
                <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-5 py-4 font-mono text-sm">{(t.date || "").slice(0, 10)}</td>
                  <td className="px-5 py-4"><Link to={`/allenamento/${t.id}`} data-testid={`training-row-${t.id}`} className="font-medium hover:underline">{t.title}</Link></td>
                  <td className="px-5 py-4 font-mono text-sm">{t.duration}′</td>
                  <td className="px-5 py-4"><span className="text-[11px] uppercase tracking-wider px-2 py-1 bg-zinc-100 rounded">{t.intensity}</span></td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`edit-list-${t.id}`}><Pencil className="w-3 h-3 mr-1" />Modifica</Button>
                      <RecurrenceDialog training={t} onDone={load}
                        trigger={<Button variant="outline" size="sm" data-testid={`rec-list-${t.id}`}><Repeat className="w-3 h-3 mr-1" />Ricorrenza</Button>} />
                      <Button variant="outline" size="sm" onClick={() => duplicateNextWeek(t)} data-testid={`dup-next-list-${t.id}`}><Copy className="w-3 h-3 mr-1" />+7 giorni</Button>
                      <DuplicateTrainingDialog training={t} onDone={load} trigger={<Button variant="outline" size="sm" data-testid={`dup-list-${t.id}`}><Copy className="w-3 h-3 mr-1" />Duplica</Button>} />
                      <Button variant="outline" size="sm" onClick={() => askDelete(t)} data-testid={`del-list-${t.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-3 h-3 mr-1" />Elimina</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {listSorted.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-500">Nessun allenamento.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Quick detail (Settimana/Mese) — riusa dialog edit + safe-delete */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="training-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'allenamento?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" del {(deleteTarget?.date || "").slice(0, 10)} verrà eliminato definitivamente. L'operazione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="del-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="del-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
